import { ChevronRight, Save, LoaderCircle, UserPen } from "lucide-react";
import React, { useState, useEffect, useContext } from "react";
import { apiRequest } from "src/async/apiUtils";
import TravelFormInput from "src/components/travel/form/TravelFormInput";
import TravelFormSelect from "src/components/travel/form/TravelFormSelect";
import { classNames } from "src/helpers/classNames";
import { formatDateToAPI, getEmergencyFields, getPersonalFields, INITIAL_STATE, parseDateFromAPI } from "src/helpers/travelHelper";
import TravelFromSkeleton from "./TravelFromSkeleton";
import TravelPassportDocuments from "./TravelPassportDocuments";
import { toast } from "react-toastify";
import TravelPreferences from "./TravelPreferences";
import TravelPayments from "./TravelPayments";
import { WorkspaceContext } from "src/context/workspace";

const FORM_SECTIONS = [
  { id: "general", title: "Traveller Details" },
  { id: "emergency", title: "Emergency Contact" },
  { id: "passport", title: "Travel Documents" },
  { id: "frequentFlyer", title: "Frequent Flyer Details" },
  // { id: "payments", title: "Payments" },
];

const TravellerProfileForm = ({ page: propPage, onBack }) => {
  const page = propPage;
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [companies, setCompanies] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("general");
  const [preFerencesData, setPreFerencesData] = useState({});
  const [errors, setErrors] = useState({});
  const { details: workspaceDetails } = useContext(WorkspaceContext);

  const fetchData = async () => {
    setIsFetching(true);
    try {
      const [companiesRes, travellerRes, preferenRes] = await Promise.all([
        apiRequest("get", `/portal/workspaces/${workspaceDetails?._id}/travel/profiles/companies?page=${page}`).catch(() => null),
        apiRequest("get", `/portal/workspaces/${workspaceDetails?._id}/travel/profiles/travellers?page=${page}`).catch(() => null),
        apiRequest("get", `/portal/workspaces/${workspaceDetails?._id}/travel/profiles/system-reference-data?page=${page}`).catch(() => null),
      ]);

      if (companiesRes?.data?.data) {
        setCompanies(companiesRes.data.data.map((item) => ({ value: item.uuid, label: item.name })));
      }

      if (preferenRes?.data?.data) {
        setPreFerencesData(preferenRes.data.data);
      }

      const apiData = travellerRes?.data?.data;
      if (apiData) {
        const gData = apiData.data?.generalData || {};
        const emgContact = apiData.data?.contacts?.emergencyContact || {};
        const papers = apiData.data?.papers || { passports: [], visas: [], idCards: [] };
        const memberships = apiData.data?.memberships || { flight: [], hotel: [], rentalCar: [] };
        const payments = apiData.data?.creditCardInfo?.creditCards;
        const preferences = apiData.data?.preferences || {};

        const mapPapers = (arr) =>
          (arr || []).map((p) => ({
            ...p,
            issueDate: parseDateFromAPI(p.issueDate),
            expiration: parseDateFromAPI(p.expiration),
          }));

        setFormData({
          username: apiData.username || "",
          firstname: apiData.firstname || "",
          middlename: apiData.middlename || "",
          name: apiData.name || "",
          companyUuid: apiData.data?.company?.uuid || "",
          birthdate: parseDateFromAPI(gData.birthdate),
          sex: gData.sex && gData.sex !== "UNDEF" ? gData.sex : gData.sex || "",
          mobilePhone: gData.mobilePhone || "",
          nationality: gData.nationality || "",
          language: gData.language || "de_CH",
          privatePhone: gData.privatePhone || "",
          businessPhone: gData.businessPhone || "",
          title: gData.greeting?.compoundTitle || "",
          email: apiData.email || gData.email || "",
          emgFirstname: emgContact.firstname || "",
          emgLastname: emgContact.lastname || "",
          emgPhone: emgContact.phone || "",
          emgEmail: emgContact.email || "",
          memberships,
          payments,
          papers: {
            passports: mapPapers(papers.passports),
            visas: mapPapers(papers.visas),
            idCards: mapPapers(papers.idCards),
          },
          preferences: {
            seatRequest: preferences.preferredSeat || "",
            mealRequest: preferences.preferredFood || "",
            smoker: !!preferences.smoker,
          },
        });
      }
    } catch (error) {
      console.error("Failed to load profile data:", error);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (workspaceDetails?._id && page) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceDetails?._id, page]);

  const handleChange = (e, field) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    let errorMsg = null;
    if (field?.required && (!value || value.toString().trim() === "")) {
      errorMsg = `${field.label || "This field"} is required`;
    } else if (field?.type === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errorMsg = "Please enter a valid e-mail address";
    } else if (field?.name === "birthdate" && value) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(value);
      if (selected > today) {
        errorMsg = "Date of Birth cannot be in the future";
      }
    }

    setErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const errorMessages = [];
    let firstErrorSection = null;

    const personalFields = getPersonalFields({ companies, countries: preFerencesData?.countries?.map((item) => ({ value: item.code, label: item.name })) });
    personalFields.forEach((field) => {
      const val = formData[field.name];
      if (field.required) {
        if (!val || val.toString().trim() === "") {
          newErrors[field.name] = `${field.label} is required`;
          errorMessages.push(`${field.label} is required`);
          if (!firstErrorSection) firstErrorSection = "general";
        }
      }
      if (field.type === "email" && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        newErrors[field.name] = "Please enter a valid e-mail address";
        errorMessages.push("Please enter a valid e-mail address");
        if (!firstErrorSection) firstErrorSection = "general";
      }
      if (field.name === "birthdate" && val) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selected = new Date(val);
        if (selected > today) {
          newErrors[field.name] = "Date of Birth cannot be in the future";
          errorMessages.push("Date of Birth cannot be in the future");
          if (!firstErrorSection) firstErrorSection = "general";
        }
      }
    });

    if (formData.emgEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emgEmail)) {
      newErrors.emgEmail = "Please enter a valid emergency contact email address";
      errorMessages.push("Please enter a valid emergency contact email address");
      if (!firstErrorSection) firstErrorSection = "emergency";
    }

    (formData.papers?.passports || []).forEach((item, index) => {
      const num = index + 1;
      if (!item.country?.trim()) {
        errorMessages.push(`Passport #${num}: Nationality is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.number?.trim()) {
        errorMessages.push(`Passport #${num}: Passport No. is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.expiration?.trim()) {
        errorMessages.push(`Passport #${num}: Expiry date is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
    });

    (formData.papers?.visas || []).forEach((item, index) => {
      const num = index + 1;
      if (!item.country?.trim()) {
        errorMessages.push(`Visa #${num}: Country is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.number?.trim()) {
        errorMessages.push(`Visa #${num}: Number is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.expiration?.trim()) {
        errorMessages.push(`Visa #${num}: Expiry date is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
    });

    (formData.papers?.idCards || []).forEach((item, index) => {
      const num = index + 1;
      if (!item.country?.trim()) {
        errorMessages.push(`ID Card #${num}: Country is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.number?.trim()) {
        errorMessages.push(`ID Card #${num}: Number is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.expiration?.trim()) {
        errorMessages.push(`ID Card #${num}: Expiry date is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
    });
    (formData.memberships?.flight || []).forEach((item, index) => {
      const num = index + 1;
      if (!item.alliance?.trim()) {
        errorMessages.push(`Flight Program #${num}: Airline is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
      if (!item.memberNumber?.trim()) {
        errorMessages.push(`Flight Program #${num}: Number is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
    });

    (formData.memberships?.hotel || []).forEach((item, index) => {
      const num = index + 1;
      if (!item.alliance?.trim()) {
        errorMessages.push(`Hotel Program #${num}: Hotel Chain is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
      if (!item.memberNumber?.trim()) {
        errorMessages.push(`Hotel Program #${num}: Customer Number is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
    });

    (formData.memberships?.rentalCar || []).forEach((item, index) => {
      const num = index + 1;
      if (!item.alliance?.trim()) {
        errorMessages.push(`Car Program #${num}: Company is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
      if (!item.memberNumber?.trim()) {
        errorMessages.push(`Car Program #${num}: Customer Number is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
    });

    setErrors(newErrors);

    return {
      isValid: errorMessages.length === 0,
      errorMessages,
      firstErrorSection,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateForm();
    if (!validation?.isValid) {
      if (validation?.firstErrorSection) {
        setActiveSection(validation?.firstErrorSection);
      }

      if (validation?.errorMessages.length === 1) {
        toast.error(validation?.errorMessages[0]);
      } else {
        const ToastErrorContent = () => (
          <div className="flex flex-col gap-1">
            <span className="font-bold text-red-700">Please correct the following errors:</span>
            <ul className="list-disc space-y-0.5 pl-4 text-xs">
              {validation?.errorMessages?.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </div>
        );
        toast.error(<ToastErrorContent />, { autoClose: 5000 });
      }
      return;
    }

    setIsSaving(true);

    const formatPapers = (arr) =>
      (arr || []).map((p) => ({
        ...p,
        issueDate: formatDateToAPI(p.issueDate),
        expiration: formatDateToAPI(p.expiration),
      }));

    const payload = {
      username: formData.username,
      firstname: formData.firstname,
      middlename: formData.middlename,
      name: formData.name,
      data: {
        contacts: {
          emergencyContact: {
            firstname: formData.emgFirstname,
            lastname: formData.emgLastname,
            phone: formData.emgPhone,
            email: formData.emgEmail,
          },
        },
        generalData: {
          birthdate: formatDateToAPI(formData.birthdate),
          sex: formData.sex,
          mobilePhone: formData.mobilePhone,
          nationality: formData.nationality,
          language: formData.language,
          privatePhone: formData.privatePhone,
          businessPhone: formData.businessPhone,
          greeting: { compoundTitle: formData.title },
          email: formData.email,
        },
        company: { uuid: formData.companyUuid },
        memberships: formData.memberships,
        creditCardInfo: {
          creditCards: formData.payments,
          webCard: { "@type": "none" },
          hotelGuarantee: { "@type": "none" },
          carGuarantee: { "@type": "none" },
          railFormOfPayment: { "@type": "none" },
          additionalFormOfPayment: "UNDEFINED",
        },
        papers: {
          passports: formatPapers(formData.papers.passports),
          visas: formatPapers(formData.papers.visas),
          idCards: formatPapers(formData.papers.idCards),
        },
        preferences: {
          preferredSeat: formData.preferences?.seatRequest || "",
          preferredFood: formData.preferences?.mealRequest || "",
          smoker: formData.preferences?.smoker || false,
        },
      },
    };

    try {
      const method = "put";
      await apiRequest(method, `/portal/workspaces/${workspaceDetails?._id}/travel/profiles/travellers?page=${page}`, { body: payload });
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Form submit failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      key: field.name,
      label: field.label,
      name: field.name,
      value: formData[field.name],
      onChange: (e) => handleChange(e, field),
      required: field.required,
      className: field.col,
      placeholder: field.placeholder,
      error: errors[field.name],
    };

    return field.type === "select" ? (
      <TravelFormSelect
        {...commonProps}
        options={field.options || []}
        select_type={field?.select_type}
      />
    ) : (
      <TravelFormInput
        {...commonProps}
        type={field.type || "text"}
        maxDate={field.maxDate}
        minDate={field.minDate}
      />
    );
  };

  const renderActiveFormContent = () => {
    switch (activeSection) {
      case "general":
        return getPersonalFields({ companies, countries: preFerencesData?.countries?.map((item) => ({ value: item.code, label: item.name })) }).map(renderField);
      case "emergency":
        return getEmergencyFields().map(renderField);
      case "passport":
        return (
          <TravelPassportDocuments
            formData={formData}
            setFormData={setFormData}
            countries={preFerencesData?.countries?.map((item) => ({ value: item.code, label: item.name })) || []}
          />
        );
      case "frequentFlyer":
        return (
          <TravelPreferences
            formData={formData}
            setFormData={setFormData}
            preferencesData={preFerencesData}
          />
        );
      case "payments":
        return (
          <TravelPayments
            formData={formData}
            setFormData={setFormData}
          />
        );
      default:
        return null;
    }
  };

  const activeSectionData = FORM_SECTIONS.find((s) => s.id === activeSection);

  return (
    <div className={classNames("px-8 py-8 sm:px-12 md:px-20 md:py-12 lg:px-32 xl:px-40", !!onBack ? "mb-8" : "")}>
      <div className="mb-10 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight text-[#2D0B31]">Traveller Profile</h2>
          <p className="mt-1 text-sm text-gray-500">Manage your details, travel documents, and preferences.</p>
        </div>
        {/* {!!onBack && (
          <button
            type="button"
            onClick={onBack}
            disabled={isFetching || isSaving}
            className="flex min-w-[140px] items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-[#2D0B31] shadow-sm transition-all duration-300 hover:bg-gray-50 hover:shadow disabled:cursor-not-allowed disabled:opacity-50">
            ← Back to Dashboard
          </button>
        )} */}
      </div>

      {isFetching ? (
        <TravelFromSkeleton />
      ) : (
        <form
          onSubmit={handleSubmit}
          noValidate
          className="relative">
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
            <div className="sticky top-6 flex flex-col gap-3 lg:col-span-4 xl:col-span-4">
              {FORM_SECTIONS.map((section) => {
                const isActive = activeSection === section.id;

                return (
                  <div
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={classNames(
                      "flex cursor-pointer items-center justify-between rounded-lg p-5 transition-all duration-200",
                      isActive ? "border border-l-4 border-gray-200 border-l-[#2D0B31] bg-gray-50 shadow-md ring-1 ring-black/5" : "border border-gray-100 bg-white shadow-sm hover:border-[#2D0B31]/30 hover:shadow-md",
                    )}>
                    <h3 className={classNames("text-lg transition-colors", isActive ? "font-extrabold text-[#2D0B31]" : "font-bold text-gray-600")}>{section.title}</h3>

                    <ChevronRight size={16} />
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col gap-6 lg:col-span-8 xl:col-span-8">
              <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm md:p-8">
                <div className="mb-8 flex items-center gap-3 border-b border-gray-50 pb-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#2D0B31]/10 text-[#2D0B31]">
                    <UserPen size={20} />
                  </div>
                  <h3 className="text-2xl font-extrabold text-[#2D0B31]">{activeSectionData?.title}</h3>
                </div>

                <div className="animate-fade-in slide-in-from-bottom-2 grid grid-cols-1 gap-6 duration-300 md:grid-cols-12">{renderActiveFormContent()}</div>
              </div>

              <div className="flex items-center justify-end gap-3 rounded-lg border border-gray-100 bg-white px-6 py-4 shadow-sm">
                {!!onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    disabled={isSaving}
                    className="rounded-lg border border-gray-200 px-6 py-2.5 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50">
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex min-w-[140px] items-center justify-center gap-2 rounded-lg bg-[#2D0B31] px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all duration-300 hover:bg-[#4a1352] hover:shadow-lg focus:ring-4 focus:ring-[#2D0B31]/30 disabled:cursor-not-allowed disabled:opacity-80">
                  {isSaving ? (
                    <>
                      <LoaderCircle
                        size={16}
                        className="animate-spin"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default TravellerProfileForm;
